// eslint-disable-next-line no-restricted-imports
import { useGetMembersDetailsQuery } from '@thoughtspot/teams/lib/src/services/generated/graphql-types';
import _ from 'lodash';
import { useMutation } from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    InviteUsersDocument,
    InviteUsersMutation,
    InviteUsersMutationVariables,
    MarkChecklistItemsDocument,
    MarkChecklistItemsMutation,
    MarkChecklistItemsMutationVariables,
    SharableListInput,
    useGetChecklistItemsQuery,
    useGetNumUsersInOrgQuery,
    useGetPurchaseOptionFromMembersDetailsLazyQuery,
    useGetSpotbuildGeneratedIdsQuery as _useGetSpotbuildGeneratedIdsQuery,
    useGetVaultTokenQuery,
    useValidateEmailLazyQuery as _useValidateEmailLazyQuery,
    useValidateEmailQuery as _useValidateEmailQuery,
} from '../generated/graphql-types';

export const useValidateEmailQuery = (emailId: string) => {
    const { data, error, loading } = _useValidateEmailQuery({
        variables: {
            emailId,
        },
    });
    return {
        error,
        loading,
        data: data?.validateEmail,
    };
};

export const useValidateEmailLazyQuery = () => {
    const [_validate, { data, error, loading }] = _useValidateEmailLazyQuery({
        fetchPolicy: 'network-only',
    });
    const validate = (emailId: string) => {
        return _validate({
            variables: {
                emailId,
            },
        });
    };
    return {
        validate,
        error,
        loading,
        data: data?.validateEmail,
    };
};

export const useInviteUsersMutation = (onInviteComplete: any = _.noop) => {
    const [inviteUsers, { data, error, loading }] = useMutation<
        InviteUsersMutation,
        InviteUsersMutationVariables
    >(InviteUsersDocument, {
        onCompleted: () => {
            onInviteComplete();
        },
    });
    return {
        data: data?.Trial__inviteUsers,
        error,
        loading,
        inviteUsers: (
            emailIds: string[],
            optionalMsg: string,
            isInviteV2: boolean,
            sharables?: SharableListInput,
        ) =>
            inviteUsers({
                variables: {
                    emailIds,
                    optionalMsg,
                    sharables,
                    isInviteV2,
                },
            }),
    };
};

export const useGetPurchaseOptionFromMemberDetails = () => {
    const [
        getMembersDetails,
        { data, error, loading },
    ] = useGetPurchaseOptionFromMembersDetailsLazyQuery({
        fetchPolicy: 'network-only',
    });
    return {
        getPurchaseOptionForMemberDetails: getMembersDetails,
        error,
        loading,
        data: data?.getMembersDetails,
    };
};

export const useGetMembersDetails = () => {
    const { data, error, loading, refetch } = useGetMembersDetailsQuery({});
    return {
        data,
        loading,
        error,
        refetch,
    };
};

export const useGetNumUsersInOrg = () => {
    const { data, error, loading, refetch } = useGetNumUsersInOrgQuery({
        fetchPolicy: 'network-only',
    });

    return {
        data,
        loading,
        error,
        refetch,
    };
};

export const useGetVaultToken = (vaultKey: string) => {
    const { data, error, loading } = useGetVaultTokenQuery({
        fetchPolicy: 'network-only',
        variables: {
            vaultKey,
        },
    });
    return {
        data: data?.getVaultToken,
        error,
        loading,
    };
};

export const useGetChecklistItems = (enableSparkflowV2: boolean) => {
    const { data, error, loading, refetch } = useGetChecklistItemsQuery({
        variables: { enableSparkflowV2 },
    });
    return {
        data: data?.getChecklistItems,
        error,
        loading,
        refetch,
    };
};

export const useMarkChecklistItems = () => {
    const [markChecklistItems, { data, error, loading }] = useMutation<
        MarkChecklistItemsMutation,
        MarkChecklistItemsMutationVariables
    >(MarkChecklistItemsDocument);
    return {
        data: data?.markChecklistItems,
        error,
        loading,
        markChecklistItems: (ids: string[]) =>
            markChecklistItems({
                variables: {
                    ids,
                },
            }),
    };
};

export const useGetSpotbuildGeneratedIds = () => {
    const { data, error, loading } = _useGetSpotbuildGeneratedIdsQuery();
    return {
        data: data?.getSpotbuildGeneratedIds,
        error,
        loading,
    };
};
